import axios from 'axios';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isLocalEnv } from '../common/functions/isLocalEnv';
import { useAuthStore } from '../common/hooks/useAuthStore';
import { useFavoriteStore } from '../common/hooks/useFavoriteStore';
import useServiceClients from '../common/hooks/useServiceClients';
import {
  IBenefit,
} from '../container/account/benefits/components/interfaces/ICommon';
import { ILiveSession, IRx } from '../container/account/rx/components/interfaces/ICommon';
import {
  IUserDetails,
  IUserFavorites,
  IUserOrder,
  IUserSubscription,
} from '../container/login/interfaces/IUserDetails';
import FavoriteService from '../services/favorites';
import UserProfileService, { UserDetailsDto } from '../services/userProfile';
import { isPageSecure } from '../utils/browserUtils';
import {
  clearSession,
  clearPersonalizedAlertStorage,
  getSession,
  PERSONALIZED_ALERT_AUTO_OPEN_KEY,
  setSession,
} from '../utils/sessionStorageUtils';
import { alertBannerType, AlertProps, useAlert } from './alert-provider';
import { setUserProfileToSession } from '../components/basic/analytics';
import { getUserAge } from './utils/userHelper';
import { omitPropsFromObj, safeGet } from '../utils/objectUtils';
import useBenefitStore from '../common/hooks/useBenefitStore';
import { AuthService } from '../services/auth';
import { getLogoutURL } from '../utils/configUtils';
import { removeCookie } from '../utils/cookieUtils';
import LogoutNotificationModal from '../components/complex/modals/logoutNotification';
import LocalesProvider from '../components/complex/header/locales-provider';
import { useConfig } from './config-provider';
import { useRouter } from '../common/hooks/useRouter';
import { splunk_message } from '../common/splunk_constant';
import useSplunkLogger from '../common/hooks/useSplunkLogger';
import LoginService, { LoginByGuidPhase, LoginProgress } from '../services/login';
import { Client } from 'graphql-sse';
import { InternalLinks } from '../common/enums/common';

export interface ILogoutOptions {
  skipRedirect?: boolean;
}
export interface IUserProfile extends IUserDetails {
  benefits: IBenefit[];
  warranties: IUserSubscription[];
  prescriptions: { active: IRx[]; expired: IRx[] };
  favorites: IUserFavorites;
  orders: IUserOrder[];
  selectedEyeglassRxId?: string;
  selectedContactsRxId?: string;
  [key: string]: any;
}

export type UserProfileProps = {
  userProfile: IUserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<IUserProfile | null>>;
  rxModalShown: boolean;
  setRxModalShown: React.Dispatch<React.SetStateAction<boolean>>;
  liveSessionData: ILiveSession | null;
  setLiveSessionData: React.Dispatch<React.SetStateAction<ILiveSession | null>>;
  logoutTimer: boolean;
  setLogoutTimer: React.Dispatch<React.SetStateAction<boolean>>;
  loginError: boolean;
  setLoginError: React.Dispatch<React.SetStateAction<boolean>>;
  loginProgress: LoginProgress | null;
  isLoggingOut: boolean;
  setIsLoggingOut: React.Dispatch<React.SetStateAction<boolean>>;
  extendedDetailsLoading: boolean;
  notifications: any[];
  notificationsLoading: boolean;
  notificationError: string | null;
  closeNotification: (messageId: string) => Promise<void>;
  resetNotificationState: () => void;
};
export const UserProfileContext = createContext<UserProfileProps | null>(null);

export function useUserProfile() {
  const ctx = useContext(UserProfileContext) as UserProfileProps;
  return ctx;
}
export const ErrorAlertLabels = {
  TTITLE: 'Authentication expired',
  BTNAME: 'ok',
};
export default function UserProvider({
  data,
  children,
}: Readonly<{
  data?: any;
  children: ReactNode;
}>): JSX.Element {
  const { config } = useConfig();
  const [loginProgress, setLoginProgress] = useState<LoginProgress>({
    stepName: 'Loading your prescriptions...',
    percentCompleted: 0,
    statusCode: 102,
  });
  const [userData, setUserData] = useState<IUserProfile | null>(null);
  const [userProfile, setUserProfile] = useState<IUserProfile | null>(null);
  const { visionSseClient, visionBffClient, visionBffMocksClient } = useServiceClients();
  const { visionBffClient: visionBffClientWithoutLoader } = useServiceClients(false);

  const [rxModalShown, setRxModalShown] = useState<boolean>(() => {
    const hasRxModalBeenShown = localStorage.getItem('hasRxModalBeenShown');
    return hasRxModalBeenShown === 'true' ? true : false;
  });
  const [loginError, setLoginError] = useState<boolean>(false);
  // Suppresses the login progress bar from reappearing right after logout, while the page
  // is still on a /secure/ URL and userProfile has just been cleared but not yet navigated away.
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [notificationReadyGuid, setNotificationReadyGuid] = useState<string | null>(null);
  const notificationUserRef = useRef<string | null>(null);
  const notificationRequestRef = useRef(0);
  const resetNotificationState = () => {
    setNotificationReadyGuid(null);
    notificationUserRef.current = null;
    setNotifications([]);
    setNotificationError(null);
    notificationRequestRef.current += 1;
  };

  // VISION - 10701
  // extendedDetailsLoading is true when we're in the process of fetching extended user details, which includes benefits, subscriptions, prescriptions, favorites, and orders.
  // We use this flag to determine whether to show a loading text on benefits section of the account page.
  // We initialize it to true if the user is already logged in or if we're on a secure page, because in both cases we will attempt to fetch extended user details on mount.
  const [extendedDetailsLoading, setExtendedDetailsLoading] = useState<boolean>(
    () => getSession('isLoggedIn') === 'true'  || isPageSecure(),
  );
  const { splunkLogger } = useSplunkLogger();
  const [liveSessionData, setLiveSessionData] = useState<ILiveSession | null>(null);
  const [prevRegion, setPrevRegion] = useState<any>(null);
  const loginService = new LoginService(visionSseClient as Client);
  const userProfileService = new UserProfileService(visionBffClient, splunkLogger);
  const userProfileServiceWithoutLoader = useMemo(
    () => new UserProfileService(visionBffClientWithoutLoader, splunkLogger),
    [splunkLogger, visionBffClientWithoutLoader],
  );
  const favoriteService = new FavoriteService(visionBffClientWithoutLoader);
  const favStore = useFavoriteStore();
  const authStore = useAuthStore();
  const { setAlertType } = useAlert() as AlertProps;
  
  const getBaseUserDetailsSafetyNetTimeout = config?.services?.getBaseUserDetailsSafetyNetTimeout;
  const getExtendedUserDetailsRetryDelay = config?.services?.getExtendedUserDetailsRetryDelay;
  const [logoutTimer, setLogoutTimer] = useState<boolean>(false);
  const logoutTimerRef = useRef<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedLocales, setSelectedLocales] = useState(data);
  const { navigate } = useRouter();

  useEffect(() => {
    const suppressOnce = sessionStorage.getItem('suppressRxModalOnce');

    if (suppressOnce === 'true') {
      console.log('UserProvider: Found suppressRxModalOnce flag on route:', location.pathname);
      sessionStorage.removeItem('suppressRxModalOnce');
      setRxModalShown(true);
      localStorage.setItem('hasRxModalBeenShown', 'true');

      splunkLogger({
        logType: 'info',
        data: {
          message: 'RxModal suppressed due to addFavorite flow (UserProvider)',
          route: window.location.pathname,
        },
      });
    }
  }, [location.pathname, splunkLogger]);

  const getBaseUserDetails = (userDetailsDto: UserDetailsDto): (() => void) | undefined => {
    splunkLogger({
      logType: 'info',
      data: splunk_message.SUCCESS.GET_BASE_USER_STARTED,
    });

    // Timeout Safety Net
    const getBaseUserDetailsTimeout = setTimeout(() => {
      setLoginError(true);
      setAlertType(alertBannerType.USER_LOGIN_TIMEOUT_ERROR);
      unsubscribe?.();
      handleLogout({ skipRedirect: true });
    }, getBaseUserDetailsSafetyNetTimeout);

    let unsubscribe: (() => void) | undefined;

    try {
      unsubscribe = loginService.subscribeLoginByGuid(
        userDetailsDto,
        payload => {
          const node = payload?.loginByGuidSse;
          if (!node) return;

          if (node.progress) {
            setLoginProgress(node.progress);
          }

          if (
            node.phase === LoginByGuidPhase.COMPLETED ||
            node.phase === LoginByGuidPhase.PARTIAL_CONTENT
          ) {
            const userData = node.userdata;

            if (!userData) {
              throw new Error('Missing user data');
            }

            if (userData.isA2Error) {
              setLoginError(true);
              setAlertType(alertBannerType.A2_PROFILE_ERROR);
              clearTimeout(getBaseUserDetailsTimeout);
              unsubscribe?.();
              handleLogout({ skipRedirect: true });
              return;
            }

            setLogoutTimer(true);
            authStore.setUserProfile(userData);
            setUserData(userData);
            setUserProfile(userData);
            const liveSessionData = safeGet(userData, 'liveSession', null);
            setLiveSessionData(liveSessionData);

            // Cache base user data so it survives full-page navigations
            // (e.g. window.location.assign from secure redirect pages)
            try {
              sessionStorage.setItem('_cachedUserData', JSON.stringify(userData));
              sessionStorage.setItem('_cachedLiveSession', JSON.stringify(liveSessionData));
            } catch (_) { /* Browser storage quota exceeded — non-critical */ }

            setUserProfileToSession({
              guid: userData.guid,
              age: getUserAge(userData.dob, splunkLogger),
            });

            setSession('fetchedBaseUserDetails', 'true');
            clearTimeout(getBaseUserDetailsTimeout);
            unsubscribe?.();

            if (
              node.phase === LoginByGuidPhase.COMPLETED &&
              userData.guid &&
              Array.isArray(userData.prescriptions?.active) &&
              Array.isArray(userData.prescriptions?.expired)
            ) {
              setNotificationReadyGuid(userData.guid);
            }

            // On secure redirect pages the full-page navigation will abort
            // this call anyway; skip it so the destination page makes the
            // single successful call via getUserAndSession instead.
            const secureRedirectPaths = [InternalLinks.secureFramesPDP, InternalLinks.addFavorite, InternalLinks.contactsSigninPdp];
            const isSecureRedirect = secureRedirectPaths.some(p => window.location.pathname.includes(p));
            if (!isSecureRedirect) {
              getExtendedUserDetails({ forceCacheReload: isLocalEnv() }, false);
            }
          }

          if (node.phase === LoginByGuidPhase.ERROR) {
            setLoginError(true);
            setAlertType(alertBannerType.USER_LOGIN_ERROR);
            clearTimeout(getBaseUserDetailsTimeout);
            unsubscribe?.();
            handleLogout({ skipRedirect: true });
          }
        },
        error => {
          console.error(error);
          setLoginError(true);
          setAlertType(alertBannerType.USER_LOGIN_ERROR);
          clearTimeout(getBaseUserDetailsTimeout);
          unsubscribe?.();
          handleLogout({ skipRedirect: true });
        },
        () => {
          clearTimeout(getBaseUserDetailsTimeout);
          unsubscribe?.();
        },
      );

      return unsubscribe;
    } catch (error) {
      splunkLogger({
        logType: 'error',
        data: { ...splunk_message.ERROR.GET_BASE_USER_ERROR, error },
      });
      setLoginError(true);
      setAlertType(alertBannerType.USER_LOGIN_ERROR);
      clearTimeout(getBaseUserDetailsTimeout);
      unsubscribe?.();
      handleLogout({ skipRedirect: true });
    }
  };

  const getExtendedUserDetails = async (
    userDetailsDto: UserDetailsDto,
    isLoaderEnabled = true,
  ): Promise<void> => {
    try {
      setExtendedDetailsLoading(true);
      splunkLogger({
        logType: 'info',
        data: splunk_message.SUCCESS.GET_EXTENDED_USER_STARTED,
      });

      const targetUserProfileService = isLoaderEnabled
        ? userProfileService
        : userProfileServiceWithoutLoader;

      const userProfileResponse =
        await targetUserProfileService.getExtendedUserDetails(userDetailsDto);
      let userProfileData = safeGet(userProfileResponse, 'getUser', null);

      const preventExtendedUserDetailsRetry = Boolean(
        getSession('preventExtendedUserDetailsRetry'),
      );

      if (!preventExtendedUserDetailsRetry) {
        setSession('preventExtendedUserDetailsRetry', 'true');
      }

      // If no retry has happened and extra details are still loading, retry after some time
      if (!preventExtendedUserDetailsRetry && userProfileData?.memberDetailLoading) {
        setTimeout(() => {
          return getExtendedUserDetails(userDetailsDto, false);
        }, getExtendedUserDetailsRetryDelay);
      } else {
        const liveSessionData = safeGet(userProfileData, 'liveSession', null);
        userProfileData = omitPropsFromObj(userProfileData, ['liveSession']);
        if (userProfileResponse?.error) {
          setAlertType(alertBannerType.USER_EXTENDED_DETAILS_ERROR);
        } else if (userProfileResponse) {
          if (userProfileData?.memberDetailsFound?.error) {
            setAlertType(alertBannerType.USER_EXTENDED_DETAILS_ERROR);
          }

          setLogoutTimer(true);
          authStore.setUserProfile(userProfileData as IUserProfile);
          setUserData(userProfileData as IUserProfile);
          setUserProfile(userProfileData as IUserProfile);
          setLiveSessionData(liveSessionData);
          getUserFavorites();

          if (userProfileData?.guid) {
            setNotificationReadyGuid(userProfileData.guid);
          }

          // Batched with setExtendedDetailsLoading(false) below (no await between them), so
          // consumers gating on "extendedDetailsLoading || notificationsLoading" never see both
          // false in between this update and the notifications-fetch effect actually starting.
          if (userProfileData?.guid && notificationUserRef.current !== userProfileData.guid) {
            setNotificationsLoading(true);
          }
        } else {
          setAlertType(alertBannerType.USER_EXTENDED_DETAILS_ERROR);
        }
        setExtendedDetailsLoading(false);
      }
    } catch (error) {
      splunkLogger({
        logType: 'error',
        data: { ...splunk_message.ERROR.GET_EXTENDED_USER_ERROR, error },
      });
      console.error(error);
      setAlertType(alertBannerType.USER_EXTENDED_DETAILS_ERROR);
      setExtendedDetailsLoading(false);
    }
  };

  const getUserFavorites = async (): Promise<void> => {
    try {
      const data = await favoriteService.getFavorites();
      if (data?.error) {
        setAlertType(data.error);
      } else if (data) {
        favStore.setUserFavorites(data);
      } else {
        setAlertType(alertBannerType.USER_FAVORITE_ERROR);
      }
    } catch (error) {
      console.error(error);
      setAlertType(alertBannerType.USER_FAVORITE_ERROR);
    }
  };

  const getUserAndSession = async () => {
    try {
      // A fresh auth check is underway, so any prior logout is no longer relevant.
      setIsLoggingOut(false);
      const isLoggedIn = getSession('isLoggedIn');

      if (!isLoggedIn || isLoggedIn === 'false') {
        setUserProfile(null);
        authStore.setUserProfile(null);
        favStore.setUserFavorites(null);
        return;
      }

      const fetchedBaseUserDetails = Boolean(getSession('fetchedBaseUserDetails'));

      if (fetchedBaseUserDetails) {
        // Hydrate state from cached base data so userProfile is non-null
        // immediately (avoids "Sign In" flicker while extended fetch runs)
        try {
          const cachedUser = sessionStorage.getItem('_cachedUserData');
          const cachedLiveSession = sessionStorage.getItem('_cachedLiveSession');
          if (cachedUser) {
            const parsedUser = JSON.parse(cachedUser);
            setLogoutTimer(true);
            authStore.setUserProfile(parsedUser);
            setUserData(parsedUser);
            setUserProfile(parsedUser);
          }
          if (cachedLiveSession) {
            setLiveSessionData(JSON.parse(cachedLiveSession));
          }
        } catch (_) { /* parse error — fall through to API fetch */ }
        sessionStorage.removeItem('_cachedUserData');
        sessionStorage.removeItem('_cachedLiveSession');

        await getExtendedUserDetails({ forceCacheReload: isLocalEnv() });
      } else {
        getBaseUserDetails({ forceCacheReload: isLocalEnv() });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (
      !notificationReadyGuid ||
      userProfile?.guid !== notificationReadyGuid ||
      notificationUserRef.current === notificationReadyGuid
    ) {
      return;
    }

    notificationUserRef.current = notificationReadyGuid;
    const requestId = ++notificationRequestRef.current;
    setNotificationsLoading(true);
    try {
      const response = await userProfileServiceWithoutLoader.getPersonalNotifications(true);
      if (requestId !== notificationRequestRef.current || notificationUserRef.current !== notificationReadyGuid) {
        return;
      }
      const nextNotifications = response?.data?.data?.getPersonalNotifications || [];
      setNotifications(nextNotifications);
      setNotificationError(null);

      if (nextNotifications.length > 0) {
        sessionStorage.setItem('suppressRxModalOnce', 'true');
        sessionStorage.setItem(PERSONALIZED_ALERT_AUTO_OPEN_KEY, 'true');
        setRxModalShown(true);
      }
    } catch (error) {
      if (requestId !== notificationRequestRef.current || notificationUserRef.current !== notificationReadyGuid) {
        return;
      }
      notificationUserRef.current = null;
      setNotifications([]);
      setNotificationError('Unable to fetch notifications at the moment.');
    } finally {
      if (requestId === notificationRequestRef.current) {
        setNotificationsLoading(false);
      }
    }
  }, [
    notificationReadyGuid,
    setRxModalShown,
    userProfile,
    userProfileServiceWithoutLoader,
  ]);

  const closeNotification = useCallback(async (messageId: string) => {
    if (!userProfile) {
      return;
    }

    setNotificationsLoading(true);
    try {
      const response = await userProfileServiceWithoutLoader.getPersonalNotifications(false, messageId);
      setNotifications(response?.data?.data?.closePersonalNotification || []);
      setNotificationError(null);
    } catch (error) {
      setNotificationError('Unable to delete notification at the moment.');
    } finally {
      setNotificationsLoading(false);
    }
  }, [userProfile, userProfileServiceWithoutLoader]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!userProfile) {
      notificationRequestRef.current += 1;
      notificationUserRef.current = null;
      setNotificationReadyGuid(null);
      setNotifications([]);
    }
  }, [userProfile]);

  // Ensure redirects navigate the top window if embedded in an iframe
  const redirectToTopWindow = (url: string) => {
    try {
      const topWin = window.top as Window | null;
      if (topWin && topWin !== window) {
        topWin.location.replace(url);
      } else {
        window.location.replace(url);
      }
    } catch (_err) {
      // Cross-origin access to window.top may throw; fallback to current window
      window.location.replace(url);
    }
  };

  const handleLogout = async (options?: ILogoutOptions): Promise<void> => {
    // Clear User Session data stored in Redis DB
    setLogoutTimer(false);
    setIsLoggingOut(true);

    await AuthService.logout(
      isLocalEnv() ? visionBffMocksClient : visionBffClientWithoutLoader,
      splunkLogger,
    );
    // Clear User Session data stored in KPD Session if any
    // if (kpdportalbff && kpdportalbff.url) {
    //   await AuthService.kpdLogout(kpdportalbff.url, kpdportalbff.timeout, splunkLogger);
    // }
    const logout = getLogoutURL(config);
    // Clear local browser state and any session maintained
    setSession('isLoggedIn', 'false');
    authStore.setUserProfile(null);
    favStore.setUserFavorites(null);
    setUserProfile(null);
    resetNotificationState();
    clearPersonalizedAlertStorage();
    setLiveSessionData(null);
    clearSession();
    useBenefitStore.getState().clearSelectedBenefit();
    setRxModalShown(() => {
      localStorage.removeItem('hasRxModalBeenShown');
      return false;
    });
    const shouldRedirect = !options?.skipRedirect;
    if (isLocalEnv()) {
      // Do not remove cookies as this will break the current eWallert impleemnmetation
      // To be doen only for local host
      removeCookie();
      if (shouldRedirect) {
        navigate(logout);
      }
    } else if (shouldRedirect) {
      redirectToTopWindow(logout);
    }
  };

  const resetTimeout = async () => {
    try {
      const userExpireyTime = await userProfileServiceWithoutLoader.getExpireyToken();
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

      // Set a new timeout
      if (logoutTimer) {
        logoutTimerRef.current = setTimeout(() => {
          setShowModal(true);
        }, userExpireyTime * 1000);
      }
    } catch (error: any) {
      splunkLogger({
        logType: 'error',
        data: {
          ...splunk_message.ERROR.TOKEN_EXPIRY_ERROR,
          code: error?.code,
          message: error?.message,
          status: error?.response?.status,
          responseErrors: error?.response?.data?.errors,
          error,
        },
      });
    }
  };

  // Add event listeners for user activity
  const handleUserActivity = () => {
    resetTimeout();
  };

  const closeUserActivity = () => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
  };
  const closeLogoutModal = () => {
    setShowModal(false);
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    window.removeEventListener('click', closeUserActivity);
    window.removeEventListener('keydown', closeUserActivity);
    window.removeEventListener('click', handleUserActivity);
    window.removeEventListener('keydown', handleUserActivity);
    handleLogout();
  };

  const onSelectedLogout = () => {
    setShowModal(false);
  };

  // Automatically close timeout modal if user login errors out
  useEffect(() => {
    if (showModal && loginError) {
      setShowModal(false);
    }
  }, [showModal, loginError]);

  useEffect(() => {
    if (logoutTimer) {
      window.addEventListener('click', handleUserActivity);
      window.addEventListener('keydown', handleUserActivity);
      resetTimeout();
    }

    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      if (logoutTimer) {
        window.removeEventListener('click', closeUserActivity);
        window.removeEventListener('keydown', closeUserActivity);
        window.removeEventListener('click', handleUserActivity);
        window.removeEventListener('keydown', handleUserActivity);
      }
    };
  }, [logoutTimer]);

  useEffect(() => {
    if (showModal) {
      window.removeEventListener('click', closeUserActivity);
      window.removeEventListener('keydown', closeUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    }
  }, [showModal]);

  useEffect(() => {
    if (isPageSecure()) {
      setSession('isLoggedIn', 'true');
    }
    if (getSession('isLoggedIn') == 'true') {
      getUserAndSession();
    }
  }, []);

  useEffect(() => {
    if (authStore.userProfile !== null) {
      setUserProfile(authStore.userProfile);
    }
  }, [authStore.userProfile]);

  useEffect(() => {
    if (userProfile) {
      setUserProfile(prev => {
        const tempUserProfile = {
          ...prev,
          selectedContactsRxId: liveSessionData?.cl_rx?.prescriptionId,
          selectedEyeglassRxId: liveSessionData?.eg_rx?.prescriptionId,
        } as IUserProfile;

        const previousRegion = sessionStorage.getItem('region');
        setPrevRegion(previousRegion);
        authStore.setUserProfile(tempUserProfile);
        return tempUserProfile;
      });
    }
  }, [userData]);

  useEffect(() => {
    if (userProfile && prevRegion) {
      if (prevRegion && prevRegion !== userProfile?.region.toUpperCase()) {
        authStore.setIsRegionChanged(true);
      } else {
        authStore.setIsRegionChanged(false);
      }
      const currentRegion = userProfile.region.toUpperCase();
      sessionStorage.setItem('region', currentRegion);
    }
  }, [prevRegion]);

  useEffect(() => {
    const server = isLocalEnv() ? 'http://localhost:3005' : '';
    const timeoutId = setInterval(async () => {
      try {
        const isLoggedIn = getSession('isLoggedIn') == 'true' || isPageSecure();
        if (isLoggedIn) {
          await axios.get(`${server}/nat/secure/keepAlive`);
          console.log('Success in keeplive');
        }
      } catch (err) {
        console.log('keepAlive', err);
        console.log('Error in keeplive', err);
      }
    }, 720000);
    // Cleanup function to clear the timeout if the component unmounts
    return () => clearInterval(timeoutId);
  }, []); // Empty dependency array ensures the effect runs only once

  //Sync to localStorage
  useEffect(() => {
    //Only set in localStorage if true, so we can remove the key from storage on logout when it's set to false
    if (rxModalShown) {
      localStorage.setItem('hasRxModalBeenShown', rxModalShown.toString());
    }
  }, [rxModalShown]);

  //Listen for changes from other tabs
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'hasRxModalBeenShown') {
        console.log('Storage event detected for hasRxModalBeenShown:', event.newValue);
        setRxModalShown(event.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const userProfileContextValue = useMemo(
    () => ({
      userProfile,
      setUserProfile,
      rxModalShown,
      setRxModalShown,
      liveSessionData,
      setLiveSessionData,
      logoutTimer,
      setLogoutTimer,
      loginError,
      setLoginError,
      loginProgress,
      isLoggingOut,
      setIsLoggingOut,
      extendedDetailsLoading,
      notifications,
      notificationsLoading,
      notificationError,
      closeNotification,
      resetNotificationState,
    }),
    [
      userProfile,
      setUserProfile,
      rxModalShown,
      setRxModalShown,
      liveSessionData,
      setLiveSessionData,
      logoutTimer,
      setLogoutTimer,
      loginError,
      setLoginError,
      loginProgress,
      isLoggingOut,
      setIsLoggingOut,
      extendedDetailsLoading,
      notifications,
      notificationsLoading,
      notificationError,
      closeNotification,
      resetNotificationState,
    ]
  );

  return (
    <LocalesProvider value={{ selectedLocales, setSelectedLocales }}>
      <UserProfileContext.Provider value={userProfileContextValue}>
        {children}
        {showModal && (
          <LogoutNotificationModal
            isOpen={showModal}
            handleClose={closeLogoutModal}
            onSelected={onSelectedLogout}
          />
        )}
      </UserProfileContext.Provider>
    </LocalesProvider>
  );
}